import { CheckCircle, Clock, Info } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { etaApi, getErrorMessage, tripsApi } from "../../../api";
import FormCard from "../../../components/FormCard";
import InfoCard from "../../../components/InfoCard";
import LoadingBar from "../../../components/LoadingBar";
import PillButton from "../../../components/PillButton";
import RoundedEdgeButton from "../../../components/RoundedEdgeButton";
import RouteConnector from "../../../components/RouteConnector";
import { ws_url } from "../../../constants";
import "../../../CSS/CurrentOrders.scss";
import { useToast } from "../../../hooks/useToast";
import notificationSound from "../../../static/audio/notification.mp3";
import chatIcon from "../../../static/images/chat-icon.png";
import profileIcon from "../../../static/images/profile-icon.svg";
import { appendChatToIndexedDB, clearAllChatsFromIndexedDB, updateChatHistoryInIndexedDB } from "../../../utils/chatHelper";
import { calculateTimeSinceUpdate, clearStoredETA, getStoredETA, storeETA } from "../../../utils/helpers";
import { subscribeToPush } from "../../../utils/pushHelper";
import useReliableWebSocket from "../../../utils/useReliableWebSocket";
import classes from "./CurrentOrders.module.scss";

const MAX_RESPONSE_TIME = 30; // 30 seconds allowed
const DEFAULT_ESTIMATED_TIME = 7 * 60; // 7 minutes
const NO_SHOW_TIMEOUT = 1.5 * 60 * 1000; // 3 minutes

const CurrentOrders = () => {
  const navigate = useNavigate();
  const { tripId } = useParams();
  const { openToast } = useToast();

  // Refs to prevent stale closures
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const responseTimerRef = useRef(null);

  // Core state
  const [trip, setTrip] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // UI state
  const [price, setPrice] = useState(null);
  const [hasNegotiated, setHasNegotiated] = useState(false);

  // ETA related state
  const [estimatedTime, setEstimatedTime] = useState(DEFAULT_ESTIMATED_TIME);
  const [etaProgress, setEtaProgress] = useState(0);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [driverLocation, setDriverLocation] = useState(null);

  // Pickup state
  const [pickupReachedTime, setPickupReachedTime] = useState(null);
  const [showNoShowButton, setShowNoShowButton] = useState(false);

  const [remainingSeconds, setRemainingSeconds] = useState(MAX_RESPONSE_TIME);

  // Info card state
  const [infoCard, setInfoCard] = useState({
    title: "Loading...",
    subtitle: "Please wait..."
  });

  // WebSocket setup
  const tripUrl = `${ws_url}/ws/trip/${tripId}/`;

  // Driver No Show timer minutes state
  const [noShowMinutes, setNoShowMinutes] = useState(0);
  const [noShowTriggered, setNoShowTriggered] = useState(false);

  const { lastJsonMessage: tripMessage, sendJsonMessage: sendTripMessage } = useReliableWebSocket(tripUrl, {
    share: true,
    shouldReconnect: () => true,
    onError: (error) => console.error("Trip WebSocket error:", error),
    persistenceKey: `driver_trip_queue_${tripId}`,
  });

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    const audio = new Audio(notificationSound);
    audio.play().catch(err => {
      console.warn("Sound play blocked by browser until user interacts:", err);
    });
  }, []);



  // Cleanup function
  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Fetch latest eta from session storage
  useEffect(() => {
    const saved = getStoredETA(tripId);
    if (saved) {
      setEstimatedTime(saved.eta);
    } else {
      storeETA(tripId, DEFAULT_ESTIMATED_TIME);
    }
  }, [tripId]);

  // Fetch initial trip details
  useEffect(() => {
    if (!tripId) return;

    const fetchTripDetails = async () => {
      try {
        setIsLoading(true);
        const data = await tripsApi.getTrip(tripId);
        setTrip(data);
      } catch (error) {
        console.error("Error fetching trip details:", error);
        const message = getErrorMessage(error);
        openToast(message || "Failed to fetch trip details", 5000, "danger");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTripDetails();
  }, [tripId, openToast]);

  // Handle trip WebSocket messages
  useEffect(() => {
    if (!tripMessage) return;

    if (tripMessage?.type === "error_occurred") {
      const errorMessages = tripMessage.data;
      console.error("Error occurred:", errorMessages);
      openToast(errorMessages[0] || "An error occurred", 5000, "danger");
      return;
    }

    if (tripMessage?.type === "chat_message") {
      const newMessage = JSON.parse(tripMessage?.data);
      const toastMessage = (newMessage.text) ? `New Message: ${newMessage.text}` : (newMessage.image) ? `You have a new message!` : "";
      appendChatToIndexedDB(newMessage);
      playNotificationSound();
      openToast(toastMessage);
    }
    else if (tripMessage?.type === "chat_history") {
      updateChatHistoryInIndexedDB(tripMessage?.data).then((hasNewMessages) => {
        if (hasNewMessages) {
          playNotificationSound();
          openToast('You have new messages!');
        }
      }).catch(console.error);
    }

    const incoming = tripMessage?.data || {};

    setTrip(prev => {
      if (!prev) return incoming;

      return {
        ...prev,
        ...Object.fromEntries(
          Object.entries(incoming).filter(([_, value]) => value !== undefined && value !== null)
        )
      };
    });
  }, [navigate, openToast, playNotificationSound, tripMessage]);

  // Handle ETA WebSocket messages
  useEffect(() => {
    if (!tripMessage) return;

    if (tripMessage.type === "eta_fetch") {
      sendTripMessage({
        event: "eta_update",
        data: JSON.stringify({
          trip_id: tripId,
          eta: estimatedTime,
        }),
      });
    }
  }, [tripMessage, estimatedTime, tripId, sendTripMessage]);

  // Get driver location for active trips (IN_TRANSIT, PENDING_ACCEPT, DRIVER_RESPONSE, USER_CONFIRMATION)
  useEffect(() => {
    if (!["IN_TRANSIT", "PENDING_ACCEPT", "DRIVER_RESPONSE", "USER_CONFIRMATION"].includes(trip?.status) || !navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setDriverLocation({ lat: latitude, lng: longitude });
      },
      (error) => console.error("Error fetching location:", error),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [trip?.status]);

  // Fetch ETA when driver location is available
  const fetchEta = useCallback(async (startLocation, endLocation) => {
    try {
      const eta = await etaApi.calculate(startLocation, endLocation);
      const saved = getStoredETA(tripId);
      const driverOffset = saved?.driverOffset ?? 0;
      const adjustedEta = Math.max(0, eta + driverOffset);
      setEstimatedTime(adjustedEta);
      storeETA(tripId, adjustedEta, driverOffset);
    } catch (error) {
      console.error("Error fetching ETA:", error);
    }
  }, [tripId]);

  useEffect(() => {
    if (!driverLocation || !["IN_TRANSIT", "PENDING_ACCEPT", "DRIVER_RESPONSE", "USER_CONFIRMATION"].includes(trip?.status)) return;

    const startLocation = `${driverLocation.lng},${driverLocation.lat}`;
    const endLocation = trip?.from_location_lnglat || "0,0";

    fetchEta(startLocation, endLocation);
  }, [driverLocation, trip?.from_location_lnglat, trip?.status, fetchEta]);

  // Handle ETA progress timer
  useEffect(() => {
    cleanup(); // Clear any existing timers

    if (trip?.status !== "IN_TRANSIT") return;

    if (estimatedTime <= 0) {
      setTimeElapsed(0);
      setEtaProgress(100);
      return;
    }

    // Calculate initial elapsed time
    const initialElapsed = Math.min(
      calculateTimeSinceUpdate(trip.updated_time, DEFAULT_ESTIMATED_TIME),
      estimatedTime
    );

    setTimeElapsed(initialElapsed);
    setEtaProgress((initialElapsed / estimatedTime) * 100);

    // Start interval if not completed
    if (initialElapsed < estimatedTime) {
      intervalRef.current = setInterval(() => {
        setTimeElapsed(prev => {
          const next = prev + 1;
          setEtaProgress((next / estimatedTime) * 100);
          return next;
        });
      }, 1000);
    }

    return cleanup;
  }, [estimatedTime, trip?.status, trip?.updated_time, cleanup]);

  // Send ETA updates
  useEffect(() => {
    if (!tripId) return;

    sendTripMessage({
      event: "eta_update",
      data: JSON.stringify({
        trip_id: tripId,
        eta: estimatedTime,
      }),
    });
  }, [estimatedTime, tripId, sendTripMessage]);

  // Handle pickup reached state
  useEffect(() => {
    if (trip?.status === "PICKUP_REACHED" && !pickupReachedTime) {
      setPickupReachedTime(trip.updated_time ? new Date(trip.updated_time).getTime() : Date.now());
      setTimeElapsed(estimatedTime);
      setEtaProgress(100);
    }
  }, [trip?.status, trip?.updated_time, pickupReachedTime, estimatedTime]);

  // Handle no-show timer
  useEffect(() => {
    if (!pickupReachedTime || trip?.status !== "PICKUP_REACHED") {
      setShowNoShowButton(false);
      setNoShowMinutes(0);
      return;
    }

    const updateNoShow = () => {
      const timeSinceReached = Date.now() - pickupReachedTime;
      const remainingMs = NO_SHOW_TIMEOUT - timeSinceReached;

      if (remainingMs <= 0) {
        setShowNoShowButton(true);
        setNoShowMinutes(null);
        return;
      }

      setShowNoShowButton(false);
      // Convert to mm:ss
      const remainingSeconds = Math.floor(remainingMs / 1000);
      const mins = Math.floor(remainingSeconds / 60);
      const secs = remainingSeconds % 60;

      setNoShowMinutes(`${mins}:${secs.toString().padStart(2, "0")}`);
    };

    // Initial update
    updateNoShow();

    // Recalculate 
    const interval = setInterval(updateNoShow, 1000);

    return () => clearInterval(interval);
  }, [pickupReachedTime, trip?.status]);

  // Update price based on trip status and handle status-specific actions
  useEffect(() => {
    if (!trip?.status) return;

    switch (trip.status) {
      case "PENDING_ACCEPT":
        setPrice(trip.price_offer);
        break;
      case "DRIVER_RESPONSE":
        setPrice(trip.nego_offer);
        break;
      case "USER_CONFIRMATION":
        setPrice(trip.final_price);
        break;
      case "IN_TRANSIT":
        subscribeToPush();
        setPrice(trip.final_price);
        break;
      case "PICKUP_REACHED":
        setPrice(trip.final_price);
        clearStoredETA(tripId);
        break;
      case "CANCELLED":
      case "COMPLETED":
        clearStoredETA(tripId);
        break;
      default:
        setPrice(0);
    }
  }, [trip?.status, trip?.price_offer, trip?.nego_offer, trip?.final_price, tripId]);

  // Update info card content
  useEffect(() => {
    if (!trip) return;

    switch (trip.status) {
      case "DRIVER_RESPONSE":
        setInfoCard({
          title: "User Negotiation Price",
          subtitle: "User has made an offer, please respond"
        });
        break;
      case "PENDING_ACCEPT":
      case "USER_CONFIRMATION":
        setInfoCard({
          title: "Waiting for User Response",
          subtitle: "Your ride could be confirmed in any second!"
        });
        break;
      case "IN_TRANSIT":
        setInfoCard({
          title: "On The Way to Pickup",
          subtitle: `Your Estimated Time Arrival: ${Math.ceil((estimatedTime - timeElapsed) / 60)} Min`
        });
        break;
      case "PICKUP_REACHED":
        setInfoCard({
          title: "On the Way to Drop-off",
          subtitle: "Drive safe, stay focused!"
        });
        break;
      case "COMPLETED":
        setInfoCard({
          title: "Trip Completed",
          subtitle: "Thank you for your service!"
        });
        break;
      case "CANCELLED":
        setInfoCard({
          title: "Trip Cancelled",
          subtitle: "The ride was cancelled."
        });
        break;
      default:
        setInfoCard({
          title: "Unknown Status",
          subtitle: "Please wait..."
        });
    }
  }, [trip, timeElapsed, estimatedTime]);

  // Handle trip completion/cancellation navigation
  useEffect(() => {
    if (!trip?.status) return;

    if (trip.status === "COMPLETED") {
      openToast("Trip completed, redirecting back to orders page...", 3000, "success");
      clearAllChatsFromIndexedDB();
      setTimeout(() => navigate("/driver-orders"), 3000);
    } else if (trip.status === "CANCELLED") {
      if (noShowTriggered) {
        openToast("No worries! Our admin team is investigating the customer no-show. Your complaint has been received.", 3000, "success");
      } else {
        openToast("The ride was cancelled, redirecting to orders page...", 3000, "danger");
      }
      clearAllChatsFromIndexedDB();
      setTimeout(() => {
        navigate("/driver-orders");
        setNoShowTriggered(false);

      }, 3000);

    }
  }, [trip?.status, navigate, noShowTriggered, openToast]);

  // Event handlers
  const handleNegotiate = useCallback((price) => {
    sendTripMessage({
      event: "make_final_offer",
      data: JSON.stringify({
        trip_id: tripId,
        final_price: price,
      }),
    });
    setHasNegotiated(true);
  }, [tripId, sendTripMessage]);

  const handleAcceptClick = useCallback(() => {
    sendTripMessage({
      event: "accept_offer",
      trip_id: tripId,
    });
  }, [tripId, sendTripMessage]);

  const handleReachedClick = useCallback(() => {
    sendTripMessage({
      event: "pickup_reached",
      trip_id: tripId,
      trip_url: window.location.href,
    });
    setPickupReachedTime(Date.now());
    setTimeElapsed(estimatedTime);
    setEtaProgress(100);
  }, [tripId, sendTripMessage, estimatedTime]);

  const handleCompleteClick = useCallback(() => {
    sendTripMessage({
      event: "complete_trip",
      trip_id: tripId,
    });
  }, [tripId, sendTripMessage]);

  const handleAddTimeClick = useCallback((time) => {
    setEstimatedTime(prev => {
      const updated = Math.max(0, prev + time);
      const saved = getStoredETA(tripId);
      const newDriverOffset = (saved?.driverOffset ?? 0) + time;
      storeETA(tripId, updated, newDriverOffset);
      return updated;
    });
  }, [tripId]);

  const handleCancel = useCallback(() => {
    sendTripMessage({
      event: "cancel_trip",
      trip_id: tripId,
    });
  }, [tripId, sendTripMessage]);

  const handleNoShow = useCallback(() => {
    setNoShowTriggered(true);
    sendTripMessage({
      event: "cancel_ongoing_trip",
      trip_id: tripId,
    });
  }, [tripId, sendTripMessage]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);


  useEffect(() => {
    if (responseTimerRef.current) {
      clearInterval(responseTimerRef.current);
      responseTimerRef.current = null;
    }

    if (!trip || !["DRIVER_RESPONSE", "PENDING_ACCEPT", "USER_CONFIRMATION"].includes(trip.status)) {
      setRemainingSeconds(MAX_RESPONSE_TIME);
      return;
    }

    const elapsedSinceUpdate = calculateTimeSinceUpdate(trip.updated_time, 0);
    const initialRemaining = Math.max(0, MAX_RESPONSE_TIME - elapsedSinceUpdate);

    setRemainingSeconds(initialRemaining);

    // 4. Start the countdown
    responseTimerRef.current = setInterval(() => {
      setRemainingSeconds(prev => {
        // TIME IS UP
        if (prev <= 1) {
          clearInterval(responseTimerRef.current);

          // Trigger actions
          handleCancel();
          openToast("User took too long. Trip cancelled.", 3000, "danger");

          // Navigate back to orders
          setTimeout(() => navigate("/driver-orders"), 3000);

          return 0;
        }
        // DECREMENT
        return prev - 1;
      });
    }, 1000);

    // Cleanup on unmount
    return () => {
      if (responseTimerRef.current) clearInterval(responseTimerRef.current);
    };
  }, [trip?.status, trip?.updated_time, handleCancel, navigate, trip]);


  // Loading state
  if (isLoading || !trip) {
    return (
      <div className={classes.container}>
        <div className={classes.innerCardContent}>
          <p>Loading trip details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.innerCardContent}>
        {/* Title Section */}
        {["PENDING_ACCEPT", "USER_CONFIRMATION", "DRIVER_RESPONSE"].includes(trip.status) && (
          <div className={classes.titleSection}>
            <p className={classes.title}>
              {trip.status === "DRIVER_RESPONSE"
                ? "Negotiation in Progress"
                : "Waiting for User Response..."}
            </p>
            <p className={classes.subtitle}>
              {trip.status === "DRIVER_RESPONSE"
                ? "Please respond to user's negotiated price"
                : "User will respond within 30 seconds"}
            </p>
            <div className={classes.loadingBarContainer}>
              <LoadingBar
                //  isLoading={["PENDING_ACCEPT", "USER_CONFIRMATION"].includes(trip.status)} 

                progress={
                  // trip.status === "DRIVER_RESPONSE"
                  ["PENDING_ACCEPT", "USER_CONFIRMATION", "DRIVER_RESPONSE"].includes(trip.status)
                    ? (remainingSeconds / MAX_RESPONSE_TIME) * 100
                    : (isLoading ? 0 : 100)}
              />
            </div>
            {["DRIVER_RESPONSE"].includes(trip.status) && (
              <p className={classes.timer}>
                <Clock size={16} className={classes.timerIcon} />
                You have <b>{remainingSeconds}s</b> to respond
              </p>
            )}

            {["PENDING_ACCEPT", "USER_CONFIRMATION"].includes(trip.status) && (
              <p className={classes.timer}>
                <Clock size={16} className={classes.timerIcon} />
                User will respond in <b>{remainingSeconds}s</b>
              </p>
            )}
          </div>
        )}
        {/* Order info card without negotiation/cancel */}
        <FormCard className={classes.orderCard}>
          <div className={classes.cardContent}>
            <div className={classes.locationsImage}>
              <RouteConnector />
            </div>
            <div className={classes.rightContent}>
              {/* From Row */}
              <div className={classes.row}>
                <div className={classes.locationBox}>
                  <p className={classes.locationAddress}>
                    {trip.from_location || "Unknown"}
                  </p>
                </div>
                <div className={classes.paxInfo}>
                  <p className={classes.pax}>PAX {trip.pax || 0}</p>
                </div>
              </div>

              <hr className={classes.separator} />

              {/* To Row */}
              <div className={classes.row}>
                <div className={classes.locationBox}>
                  <p className={classes.locationAddress}>
                    {trip.to_location || "Unknown"}
                  </p>
                </div>
                <div className={classes.priceInfo}>
                  <p className={classes.price}>RM {Number(price).toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>
          {trip.status === "IN_TRANSIT" && (
            <>
              <p className={classes.intransitTitle}>ESTIMATED ARRIVAL</p>
              <div className={classes.intransitTimeInfo}>
                <p className={classes.instransitTime}> {Math.max(0, Math.ceil((estimatedTime - timeElapsed) / 60))} min</p>
                <div className={classes.timeButtons}>
                  <PillButton
                    className={`secondaryButton ${classes.minusTimeButton}`}
                    onClick={() => handleAddTimeClick(-60)}
                  >
                    <span>-</span>
                    <span className={classes.buttonText}>1 MIN</span>
                  </PillButton>
                  <PillButton
                    className={`secondaryButton ${classes.addTimeButton}`}
                    onClick={() => handleAddTimeClick(60)}
                  >
                    <span>+</span>
                    <span className={classes.buttonText}>1 MIN</span>
                  </PillButton>
                </div>
              </div>
              <LoadingBar
                progress={etaProgress}
              />
            </>
          )}

        </FormCard>

        <FormCard>
          <div className={classes.cardContentPadding}>
            <p className={classes.passengerName}>
              {trip.user_details?.name || "Unknown Passenger"}
            </p>

            {["PENDING_ACCEPT", "DRIVER_RESPONSE", "USER_CONFIRMATION"].includes(trip.status) && (
              <p className={classes.userStatus}>
                <span className={classes.onlineDot} />
                User is online
              </p>
            )}

            <hr className={classes.separator} />
            {/* Negotiation controls */}
            {trip.status === "DRIVER_RESPONSE" && (
              <div className={classes.negotiationSection}>

                <div className={classes.textGroup}>
                  <p className={classes.negoTitle}>User's Offer</p>
                  <p className={classes.negoSubtitle}>Requesting this price</p>
                </div>
                {/* Accept button */}
                <RoundedEdgeButton
                  className={classes.acceptButton}
                  onClick={handleAcceptClick}
                >
                  <span className={classes.btnMainText}>Accept RM {trip.nego_offer}</span>
                  <span className={classes.btnSubText}>Negotiated Offer</span>
                </RoundedEdgeButton>

                <div className={classes.counterOfferSection}>
                  <div className={classes.textGroup}>
                    <p className={classes.negoTitle}>Send Counter Offer</p>
                    <p className={classes.negoSubtitle}>Choose a price to negotiate back to the user</p>
                  </div>
                  {/* First negotiation button */}
                  {trip.price_offer - trip.nego_offer === 2 && (
                    <RoundedEdgeButton
                      disabled={hasNegotiated}
                      className={classes.negoButton1}
                      onClick={() => handleNegotiate(trip.price_offer - 1)}
                    >
                      <span className={classes.btnMainText}>RM {trip.price_offer - 1}</span>
                      <span className={classes.btnSubText}>Negotiate Again</span>
                    </RoundedEdgeButton>
                  )}

                  {/* Second negotiation button  */}
                  {trip.price_offer - trip.nego_offer >= 1 && (
                    <RoundedEdgeButton
                      disabled={hasNegotiated}
                      className={classes.negoButton2}
                      onClick={() => handleNegotiate(trip.price_offer)}
                    >
                      <span className={classes.btnMainText}>
                        RM {trip.price_offer}
                      </span>
                      <span className={classes.btnSubText}>
                        {(trip.price_offer !== trip.base_price) ? "Adjusted Fare" : "Base Fare"}
                      </span>
                    </RoundedEdgeButton>
                  )}
                </div>
              </div>
            )}

            {["PENDING_ACCEPT", "USER_CONFIRMATION"].includes(trip.status) && (
              <RoundedEdgeButton
                className={classes.cancelButton}
                onClick={handleCancel}
              >
                Cancel
              </RoundedEdgeButton>
            )}

            {["IN_TRANSIT", "PICKUP_REACHED"].includes(trip.status) && (
              <div className={classes.actionButtonsContainer}>

                {/* Chat Button */}
                <PillButton
                  className={classes.actionButton}
                  onClick={() => navigate(`/chat`)}
                >
                  <img src={chatIcon} alt="chat" className={classes.btnIcon} />
                  <span>Chat</span>
                </PillButton>

                {/* Passenger Profile Button */}
                <PillButton
                  className={classes.actionButton}
                  onClick={() => navigate(`/profile/${trip.user_details?.user_id}`, { state: { profileData: trip.user_details } })}
                >
                  <img src={profileIcon} alt="profile" className={classes.btnIcon} />
                  <span>User Profile</span>
                </PillButton>

              </div>
            )}
          </div>
        </FormCard>

        {trip.status === "USER_CONFIRMATION" && (
          <div className={classes.negotiationSuccess}>
            <CheckCircle size={16} className={classes.successIcon} />
            <span className={classes.successText}>Negotiated fare successfully sent to user</span>
          </div>
        )}

        {trip.status === "IN_TRANSIT" && (
          <RoundedEdgeButton
            className={classes.reachedButton}
            onClick={handleReachedClick} >
            <span className={classes.buttonText}>Pickup Reached</span>
          </RoundedEdgeButton>
        )}

        {/* arrived at pickup section */}
        {trip.status === "PICKUP_REACHED" && (
          <InfoCard
            className={classes.infoCard}
            title={infoCard.title}
            subtitle={infoCard.subtitle}
            //isProgressing={["PICKUP_REACHED"].includes(trip.status)}
            progress={etaProgress}
          >
            {/* PICKUP_REACHED controls */}
            {trip.status === "PICKUP_REACHED" && (
              <div className={classes.tripControlButtons}>
                <RoundedEdgeButton
                  className={classes.completeButton}
                  onClick={handleCompleteClick}
                >
                  <span className={classes.buttonText}>Complete Trip</span>
                </RoundedEdgeButton>

                <RoundedEdgeButton
                  className={classes.noShowButton}
                  disabled={!showNoShowButton}
                  onClick={handleNoShow}
                >
                  <span className={classes.buttonText}>Customer No-Show</span>
                </RoundedEdgeButton>
              </div>

            )}
            {noShowMinutes && (
              <p className={classes.noShowMessage}>
                No-Show Button will be activated after {noShowMinutes}.
              </p>
            )}

            <div className={classes.negotiationSuccess}>
              <Info size={18} className={classes.successIcon} />
              <span className={classes.successText}>We appreciate your patience</span>
            </div>
          </InfoCard>
        )}
      </div >
    </div >
  );
};

export default CurrentOrders;
